import { Navigate } from 'react-router-dom'
import { useSeccionesPlataforma } from '../hooks/useSeccionesPlataforma'
import { construirMenu, primeraRuta } from '../services/menuPlataforma'

// ══════════════════════════════════════════════════════════════════════════════════════════════
//  LAS DOS PUERTAS DE DENTRO DEL PANEL DE PLATAFORMA
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  POR QUE VIVEN EN SU PROPIO FICHERO Y NO DENTRO DE App.jsx. Porque una puerta que no se puede
//  EJECUTAR en una guardia no es una puerta: es un parrafo. Estando dentro de App.jsx —que importa
//  media aplicacion— lo unico que se podia medir de ellas era el TEXTO, con tres expresiones
//  regulares, y un `|| true` colado en el criterio dejaba la puerta abierta de par en par con todas
//  las pruebas en verde. Aqui son dos componentes pequenos con tres importaciones, asi que la
//  guardia los pinta de verdad (`plataforma-puertas-ruta.test.js`) con unas secciones y con otras y
//  mira QUE SALE. App.jsx los monta; el criterio vive aqui.
//
//  UN SOLO CRITERIO PARA CADA PUERTA. La seccion que hace falta para entrar la dice el MONTAJE
//  (`<SeccionPlataforma clave="...">` en App.jsx, cruzado con RUTA_POR_SECCION por la guardia del
//  menu), no un segundo mapa ruta->seccion escrito aparte: dos criterios para la misma puerta es
//  como se abren los agujeros, y el segundo siempre acaba siendo el que nadie mantiene.

// ── LA PUERTA DE CADA SECCION ────────────────────────────────────────────────────────────────
// `SuperadminRoute` (App.jsx) contesta a "¿es esta persona del equipo de plataforma?". Esto contesta
// a la otra mitad: "¿le han dado ESTA seccion?".
//
// POR QUE HACE FALTA AQUI SI EL SERVIDOR YA LA EXIGE. Porque son dos danos distintos. El servidor
// es quien CIERRA —y ahi no se toca nada—, pero sin este guardia, quien teclee /superadmin/portal
// sin tener esa seccion aterriza en una pantalla que se pinta entera y despues se llena de 403 sin
// explicar nada. Aqui se le dice, con el nombre en cristiano QUE PONE EL SERVIDOR, que seccion le
// falta y a quien pedirsela. Esconder la entrada del menu no cierra la puerta; esto tampoco la
// cierra: la hace entendible.
//
// Y MIENTRAS SE CARGA NO SE DECIDE. Con `listo` a false no se pinta ni la pantalla ni la negativa:
// dar por hecho "no tienes nada" durante el medio segundo de la peticion echaria de su propia
// pantalla a quien acaba de recargar con F5.
export function SeccionPlataforma({ clave, children }) {
  const { secciones, catalogo, listo, degradado, lecturaPanel } = useSeccionesPlataforma()
  if (!listo) return <div style={{ padding: '40px', color: 'var(--text-muted)' }}>Cargando…</div>
  if (secciones.includes(clave)) return children
  // EN MODO AVERIA SE PASA A MIRAR, y esta linea es la que hace que el menu no sea un decorado.
  // Durante una averia el servidor NO concede ninguna seccion (a proposito: una seccion autoriza
  // en toda la aplicacion, y por ahi se escapo tres veces), asi que `secciones` viene VACIA y esta
  // puerta cerraba LAS DIEZ pantallas del panel — con un cartel que ademas decia «No es una
  // averia», que era exactamente lo que estaba pasando. El menu se pinta entero con `lectura_panel`
  // para poder diagnosticar; sin esto lleva a diez callejones y arreglar la averia vuelve a obligar
  // a entrar por SSH, que es lo que la degradacion existe para evitar.
  //
  // LO QUE SE ABRE ES MIRAR, y solo eso: el servidor contesta 503 a TODA escritura mientras dura
  // (middleware/auth.puertaPlataforma) y sus GET contestan 200 (medido en
  // routes/plataforma-averia-solo-lectura.test.js §5). Que los controles que escriben salgan
  // apagados es la otra mitad de esta decision, y la contesta hooks/usePanelSoloLectura.js.
  if (degradado && lecturaPanel) return children
  const nombre = catalogo.find(s => s.clave === clave)?.label || clave
  return (
    <div data-puerta-seccion={clave} style={{ maxWidth: '620px', margin: '40px auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg, 16px)', padding: '26px 28px' }}>
      <div style={{ fontSize: '17px', fontWeight: 800, marginBottom: '10px' }}>Esta parte del panel no la tienes</div>
      <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Tu acceso al panel de plataforma no incluye <b>«{nombre}»</b>. No es una avería: cada parte
        del panel se concede por separado. Pídesela a quien lleve el equipo de plataforma diciéndole
        ese nombre — es el mismo que sale en su pantalla de reparto.
      </div>
    </div>
  )
}

// ── EL ATERRIZAJE ────────────────────────────────────────────────────────────────────────────
// A donde va quien entra por /superadmin a secas: a la PRIMERA ENTRADA DEL MENU QUE ESA PERSONA VE. Desde B-1251
// (12-oct-2026) es Inicio, que solo le enseña lo de sus secciones; sin ninguna, no hay a donde llevarle. Una ruta
// fija aqui mandaria a la persona de soporte, cada vez que entra a trabajar, a la pantalla de "esta parte del panel
// no la tienes".
//
// Se calcula con el MISMO menu que se pinta al lado (`construirMenu` + `primeraRuta`): si el
// aterrizaje se calculara aparte, un dia llevaria a una seccion que no sale en el menu.
export function AterrizajePlataforma() {
  const { secciones, catalogo, categorias, listo, degradado, lecturaPanel } = useSeccionesPlataforma()
  if (!listo) return <div style={{ padding: '40px', color: 'var(--text-muted)' }}>Cargando…</div>
  // CON EL PANEL AVERIADO SE ATERRIZA IGUAL, y con el MISMO menu que se pinta al lado —incluida su
  // capacidad de diagnostico—. Calculado sin ella, quien entra por /superadmin a arreglar la averia
  // aterriza en «todavia no tienes ninguna seccion»: es verdad, y no es lo que le pasa.
  const destino = primeraRuta(construirMenu({ catalogo, categorias, secciones, lecturaDeDiagnostico: degradado && lecturaPanel }))
  if (destino) return <Navigate to={destino} replace />
  return sinSecciones()
}

// ── LA PUERTA DE INICIO (B-1251, 12-oct-2026) ────────────────────────────────────────────────
// Inicio no es una sección: junta lo que pide atención en las que esa persona tiene. Se abre con la MISMA condición
// con la que el menú lo pinta (que salga alguna entrada debajo: `construirMenu` lo pone arriba solo entonces), y sin
// ninguna sección dice lo mismo que el aterrizaje. Cada tarjeta de dentro vuelve a mirar su sección.
export function PuertaInicio({ children }) {
  const { secciones, catalogo, categorias, listo, degradado, lecturaPanel } = useSeccionesPlataforma()
  if (!listo) return <div style={{ padding: '40px', color: 'var(--text-muted)' }}>Cargando…</div>
  const menu = construirMenu({ catalogo, categorias, secciones, lecturaDeDiagnostico: degradado && lecturaPanel })
  if (menu.some((m) => m.tipo === 'entrada')) return children
  return sinSecciones()
}

// Una función que DEVUELVE el aviso (no un componente): así lo que se pinta está en el árbol de la puerta y su guardia lo
// ve sin tener que desplegar componentes (routes/plataforma-puertas-ruta.test.js).
function sinSecciones() {
  return (
    <div data-aterrizaje-sin-secciones style={{ maxWidth: '620px', margin: '40px auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg, 16px)', padding: '26px 28px' }}>
      <div style={{ fontSize: '17px', fontWeight: 800, marginBottom: '10px' }}>Todavía no tienes ninguna sección</div>
      <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Tu cuenta es del equipo de plataforma, pero aún no se le ha concedido ninguna parte del panel.
        Quien lleve el equipo de plataforma te las da desde <b>Equipo de plataforma</b>.
      </div>
    </div>
  )
}
